// src/hooks/useDriverLocation.js
// GPS via Firebase Realtime Database (WebSocket persistente)
// muito mais barato que Firestore para atualizações frequentes:
//  - RTDB: cobra por GB transferido (~$1/GB)
//  - Firestore: cobra por escrita (~$0.06/100k) — GPS gera centenas de milhares/mês
//
// Intervalo adaptativo:
//  - 15s com corrida ativa (passageiro precisa ver o carro)
//  - 30s aguardando corrida (só presença no mapa)
// Filtro de distância: só grava se moveu >15m

import { useEffect, useRef } from "react";
import { ref, set, remove } from "firebase/database";
import { rtdb } from "../firebase";
import { watchLocation } from "../services/locationService";

const INTERVALO_COM_CORRIDA_MS = 15_000;
const INTERVALO_AGUARDANDO_MS  = 30_000;
const PRECISAO_MINIMA_M        = 100;
const DISTANCIA_MINIMA_M       = 15;

function distanciaMetros(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function useDriverLocation(uid, online, temCorridaAtiva = false) {
  const watchIdRef        = useRef(null);
  const ultimaGravacaoRef = useRef(0);
  const ultimaPosRef      = useRef(null);

  useEffect(() => {
    if (!uid || !online) {
      if (watchIdRef.current !== null) {
        watchIdRef.current.stop?.().catch(() => null);
        watchIdRef.current = null;
      }
      // Remove presença do RTDB ao ficar offline
      if (uid) remove(ref(rtdb, `drivers_location/${uid}`)).catch(() => {});
      return;
    }

    let active = true;
    watchLocation(
      async (position) => {
        if (!active) return;
        const { lat: latitude, lng: longitude, accuracy, speed } = position;
        if (!Number.isFinite(accuracy) || accuracy > PRECISAO_MINIMA_M) return;

        const intervalo = temCorridaAtiva ? INTERVALO_COM_CORRIDA_MS : INTERVALO_AGUARDANDO_MS;
        const agora = Date.now();
        if (agora - ultimaGravacaoRef.current < intervalo) return;

        // Só grava se moveu o suficiente
        if (ultimaPosRef.current) {
          const dist = distanciaMetros(
            ultimaPosRef.current.lat, ultimaPosRef.current.lng,
            latitude, longitude
          );
          if (dist < DISTANCIA_MINIMA_M) return;
        }

        ultimaGravacaoRef.current = agora;
        ultimaPosRef.current = { lat: latitude, lng: longitude };

        try {
          // set() no RTDB — muito mais rápido e barato que updateDoc no Firestore
          await set(ref(rtdb, `drivers_location/${uid}`), {
            lat: latitude,
            lng: longitude,
            t: agora, // timestamp em ms (ocupa menos que serverTimestamp)
            accuracy,
            ...(Number.isFinite(speed) && speed >= 0 ? { speed } : {}),
          });
        } catch (err) {
          console.warn("useDriverLocation:", err.message);
        }
      },
      (err) => { console.warn("useDriverLocation GPS:", err.message); },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 }
    ).then((controller) => {
      if (active) watchIdRef.current = controller;
      else controller.stop?.().catch(() => null);
    }).catch((err) => { console.warn("useDriverLocation GPS:", err.message); });

    return () => {
      active = false;
      if (watchIdRef.current !== null) {
        watchIdRef.current.stop?.().catch(() => null);
        watchIdRef.current = null;
      }
      // Evita presença fantasma ao desmontar/trocar de conta. O próximo
      // componente online publicará novamente a posição atual.
      remove(ref(rtdb, `drivers_location/${uid}`)).catch(() => {});
    };
  }, [uid, online, temCorridaAtiva]);
}
